import { useEffect, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';

import PageHeader from '../../components/ui/PageHeader';
import Card from '../../components/ui/Card';
import Button from '../../components/ui/Button';
import { Icon } from '../../components/ui/Icon';
import DeleteModal from '../../components/ui/DeleteModal';
import BadgeUploadModal from '../../components/ui/BadgeUploadModal';

import { badgesApi } from '../../services/api';
import apiClient from '../../services/apiClient';
import { pushToast } from '../../store/slices/uiSlice';

/**
 * BadgeImage — auth-protected badge görseli.
 * <img src> Bearer token taşıyamaz; axios ile blob çek + objectURL kullan.
 */
function BadgeImage({ badgeId, name }) {
  const [src, setSrc] = useState(null);
  const [err, setErr] = useState(false);

  useEffect(() => {
    if (!badgeId) return;
    let objectUrl;
    const controller = new AbortController();

    apiClient
      .get(`/rule-engine/badges/${badgeId}/image`, {
        responseType: 'blob',
        signal: controller.signal,
      })
      .then((r) => {
        objectUrl = URL.createObjectURL(r.data);
        setSrc(objectUrl);
      })
      .catch((e) => {
        if (e?.code !== 'ERR_CANCELED') setErr(true);
      });

    return () => {
      controller.abort();
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [badgeId]);

  if (err || !src) {
    return (
      <div className="flex h-full w-full items-center justify-center bg-neutral-100">
        <Icon name="image" className="h-8 w-8 text-neutral-300" />
      </div>
    );
  }

  return (
    <img
      src={src}
      alt={name}
      className="h-full w-full object-contain p-2"
    />
  );
}

export default function Badges() {
  const dispatch = useDispatch();

  const [badges, setBadges] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploadModalOpen, setUploadModalOpen] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null); // {id, name}
  const [deleting, setDeleting] = useState(false);
  const [editTarget, setEditTarget] = useState(null);  // badge id being edited
  const [editName, setEditName] = useState('');
  const [saving, setSaving] = useState(false);
  const editInputRef = useRef(null);

  // ── Listele ────────────────────────────────────────────────────
  const load = () => {
    setLoading(true);
    badgesApi
      .list()
      .then((r) => setBadges(Array.isArray(r.data) ? r.data : []))
      .catch(() => {
        dispatch(pushToast({ kind: 'error', message: 'Rozetler yüklenemedi.' }));
        setBadges([]);
      })
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Modal onay → upload ────────────────────────────────────────
  const handleUploadConfirm = async (file, name) => {
    setUploading(true);
    try {
      await badgesApi.upload(file, name);
      dispatch(pushToast({ kind: 'success', message: `"${name}" yüklendi.` }));
      setUploadModalOpen(false);
      load();
    } catch (err) {
      dispatch(
        pushToast({
          kind: 'error',
          message: err?.response?.data?.detail || 'Rozet yüklenemedi. Dosya tipi veya boyutu uygun değil.',
        }),
      );
    } finally {
      setUploading(false);
    }
  };

  // ── Edit ──────────────────────────────────────────────────────
  const startEdit = (badge) => {
    setEditTarget(badge.id);
    setEditName(badge.name);
    setTimeout(() => editInputRef.current?.focus(), 50);
  };

  const cancelEdit = () => {
    setEditTarget(null);
    setEditName('');
  };

  const handleSaveName = async (id) => {
    const trimmed = editName.trim();
    if (!trimmed) return;
    const badge = badges.find((b) => b.id === id);
    if (badge && trimmed === badge.name) { cancelEdit(); return; }
    setSaving(true);
    try {
      const res = await badgesApi.update(id, trimmed);
      const updated = res.data?.data ?? res.data;
      setBadges((prev) => prev.map((b) => (b.id === id ? { ...b, name: updated.name ?? trimmed } : b)));
      dispatch(pushToast({ kind: 'success', message: `Ad güncellendi: "${updated.name ?? trimmed}"` }));
      setEditTarget(null);
    } catch {
      dispatch(pushToast({ kind: 'error', message: 'Ad güncellenemedi.' }));
    } finally {
      setSaving(false);
    }
  };

  // ── Delete ─────────────────────────────────────────────────────
  const handleDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await badgesApi.remove(deleteTarget.id);
      dispatch(pushToast({ kind: 'success', message: `"${deleteTarget.name}" silindi.` }));
      setBadges((prev) => prev.filter((b) => b.id !== deleteTarget.id));
      setDeleteTarget(null);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      dispatch(pushToast({
        kind: 'error',
        message: detail || 'Rozet silinemedi.',
      }));
    } finally {
      setDeleting(false);
    }
  };

  // ── Render ─────────────────────────────────────────────────────
  return (
    <div className="flex flex-col gap-6">
      <PageHeader
        title="Rozetler"
        subtitle="Dönem ödülü olarak kullanıcılara verilebilecek rozet görselleri"
        className="bg-white border border-[#E1E1E1] shadow-card rounded-[1.25rem] px-6 py-4"
        actions={
          <Button icon="upload" onClick={() => setUploadModalOpen(true)}>
            Rozet Yükle
          </Button>
        }
      />

      <Card>
        {loading ? (
          <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6">
            {Array.from({ length: 12 }).map((_, i) => (
              <div
                key={i}
                className="animate-pulse rounded-xl border border-neutral-200 bg-neutral-100"
                style={{ aspectRatio: '1' }}
              />
            ))}
          </div>
        ) : badges.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-16 gap-4 text-center">
            <div className="flex h-16 w-16 items-center justify-center rounded-full bg-purple-50">
              <Icon name="badge" className="h-8 w-8 text-purple-400" />
            </div>
            <div>
              <p className="text-sm font-medium text-neutral-700">Henüz rozet yüklenmedi</p>
              <p className="mt-1 text-xs text-neutral-400">
                PNG, JPG, GIF veya WebP formatında dosya yükleyin (maks. 2 MB)
              </p>
            </div>
            <Button icon="upload" onClick={() => setUploadModalOpen(true)}>
              İlk Rozeti Yükle
            </Button>
          </div>
        ) : (
          <>
            <div className="mb-4 flex items-center justify-between">
              <p className="text-sm text-neutral-500">
                <span className="font-semibold text-neutral-800">{badges.length}</span> rozet
              </p>
              <p className="text-xs text-neutral-400">PNG · JPG · GIF · WebP · maks. 2 MB</p>
            </div>
            <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6">
              {badges.map((badge) => (
                <div
                  key={badge.id}
                  className="group relative flex flex-col gap-2 rounded-xl border border-neutral-200 bg-white p-2 transition-shadow hover:shadow-md"
                >
                  {/* Görsel */}
                  <div
                    className="relative overflow-hidden rounded-lg bg-neutral-50"
                    style={{ aspectRatio: '1' }}
                  >
                    <BadgeImage badgeId={badge.id} name={badge.name} />
                    {/* Aksiyonlar — hover'da görünür */}
                    <div className="absolute right-1.5 top-1.5 hidden gap-1 group-hover:flex">
                      <button
                        type="button"
                        onClick={() => startEdit(badge)}
                        className="flex h-7 w-7 items-center justify-center rounded-full bg-white/90 shadow-sm text-neutral-500 hover:text-primary-600 transition-colors"
                        title="Adı düzenle"
                      >
                        <Icon name="edit" className="h-3.5 w-3.5" />
                      </button>
                      <button
                        type="button"
                        onClick={() => setDeleteTarget({ id: badge.id, name: badge.name })}
                        className="flex h-7 w-7 items-center justify-center rounded-full bg-white/90 shadow-sm text-neutral-500 hover:text-red-600 transition-colors"
                        title="Rozeti sil"
                      >
                        <Icon name="trash" className="h-3.5 w-3.5" />
                      </button>
                    </div>
                  </div>

                  {/* Ad + meta */}
                  <div className="px-0.5 pb-0.5">
                    {editTarget === badge.id ? (
                      <div className="flex items-center gap-1">
                        <input
                          ref={editInputRef}
                          value={editName}
                          onChange={(e) => setEditName(e.target.value)}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') handleSaveName(badge.id);
                            if (e.key === 'Escape') cancelEdit();
                          }}
                          disabled={saving}
                          className="min-w-0 flex-1 rounded border border-primary-400 px-1.5 py-0.5 text-xs font-semibold text-neutral-800 outline-none focus:ring-1 focus:ring-primary-300 disabled:opacity-50"
                        />
                        <button
                          type="button"
                          onClick={() => handleSaveName(badge.id)}
                          disabled={saving || !editName.trim()}
                          className="flex h-5 w-5 flex-shrink-0 items-center justify-center rounded bg-primary-600 text-white hover:bg-primary-700 disabled:opacity-40"
                          title="Kaydet"
                        >
                          <Icon name="check" className="h-3 w-3" />
                        </button>
                        <button
                          type="button"
                          onClick={cancelEdit}
                          disabled={saving}
                          className="flex h-5 w-5 flex-shrink-0 items-center justify-center rounded bg-neutral-200 text-neutral-600 hover:bg-neutral-300 disabled:opacity-40"
                          title="İptal"
                        >
                          <Icon name="x" className="h-3 w-3" />
                        </button>
                      </div>
                    ) : (
                      <p className="truncate text-xs font-semibold text-neutral-800" title={badge.name}>
                        {badge.name}
                      </p>
                    )}
                    {badge.created_at && (
                      <p className="text-[10px] text-neutral-400">
                        {new Date(badge.created_at).toLocaleDateString('tr-TR', {
                          day: '2-digit',
                          month: 'short',
                          year: 'numeric',
                        })}
                      </p>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </>
        )}
      </Card>

      {/* Rozet yükleme modalı */}
      <BadgeUploadModal
        open={uploadModalOpen}
        uploading={uploading}
        onConfirm={handleUploadConfirm}
        onCancel={() => !uploading && setUploadModalOpen(false)}
        onFileError={(msg) => dispatch(pushToast({ kind: 'error', message: msg }))}
      />

      {/* Silme onay modalı */}
      <DeleteModal
        open={!!deleteTarget}
        title="Rozeti silmek istediğinize emin misiniz?"
        description={
          deleteTarget
            ? `"${deleteTarget.name}" kalıcı olarak silinecek. Bu rozeti kullanan dönem ödülleri etkilenebilir.`
            : ''
        }
        loading={deleting}
        onConfirm={handleDelete}
        onCancel={() => setDeleteTarget(null)}
      />
    </div>
  );
}
